import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { JustForYouTag } from "@/atoms/tag/JustForYouTag";
import { TextEffect } from "@/components/ui/motion-primitives/text-effect";
import {
  MembershipDetailsData,
  PackageDetailsData,
} from "@/services/CatalogTypes";
import { ImagePreloader } from "atoms/image-preloader/ImagePreloader";

import { useAdonisChat } from "../../AdonisChatProvider/AdonisChatProvider";
import { useCatalogService } from "../../hooks/useCatalogService";

export interface AdonisProductRecommendationCardProps {
  imageUrl: string;
  title: string;
  note?: string | null; // Tweet-length rationale from AI
  priceCents?: number | null;
  memberPriceCents?: number | null;
  sectionMinHeights?: {
    description?: number;
    note?: number;
  };
  href?: string | null;
  LinkComponent?: (props: {
    to: string;
    children: React.ReactNode;
    className?: string;
    onClick?: () => void;
  }) => JSX.Element;
}

export const AdonisProductRecommendationCard = ({
  imageUrl,
  title,
  note,
  priceCents,
  memberPriceCents,
  sectionMinHeights,
  href,
  LinkComponent,
}: AdonisProductRecommendationCardProps) => {
  const catalogService = useCatalogService();
  const location = useLocation();
  const { closeChat } = useAdonisChat();
  const [enrichedImageUrl, setEnrichedImageUrl] = useState<string>(imageUrl);

  // Enrich image if empty
  useEffect(() => {
    const needsImage = !imageUrl || imageUrl.trim() === "";
    const hrefValue = href ?? "";
    if (!needsImage || !hrefValue) {
      return;
    }

    let isActive = true;
    const enrich = async () => {
      try {
        if (hrefValue.startsWith("/pdp/package/")) {
          const details: PackageDetailsData =
            await catalogService.fetchEssentialPackageDetails(hrefValue);
          const listingImage = details?.listing?.imageUrl;
          if (listingImage && isActive) {
            setEnrichedImageUrl(listingImage);
          }
        } else if (hrefValue.startsWith("/pdp/membership/")) {
          const id = hrefValue.replace(/^\/pdp\/membership\//, "");
          const details: MembershipDetailsData =
            await catalogService.fetchEssentialMembershipDetails(id);
          const membershipImage = details?.membership?.imageUrl;
          if (membershipImage && isActive) {
            setEnrichedImageUrl(membershipImage);
          }
        }
      } catch {
        // Ignore enrichment errors; placeholder will render
      }
    };

    enrich();
    return () => {
      isActive = false;
    };
  }, [imageUrl, href, catalogService]);

  const hasPrices = typeof priceCents === "number" && priceCents > 0;

  const formatMoney = (cents?: number | null) => {
    if (typeof cents !== "number") return undefined;
    const dollars = cents / 100;
    const hasCents = cents % 100 !== 0;
    return hasCents ? `$${dollars.toFixed(2)}` : `$${Math.floor(dollars)}`;
  };

  // Handle click on cards - close chat if navigating to same URL
  const handleClick = () => {
    if (href && href === location.pathname) {
      closeChat();
    }
  };

  const cardContent = (
    <div
      className={`flex h-full flex-col gap-2 overflow-hidden rounded-lg border border-[#AF936E] bg-white pt-3 shadow-md ${
        !hasPrices ? "pb-2" : ""
      }`}
    >
      <JustForYouTag variant="in-carousel-card" />
      <ImagePreloader
        url={enrichedImageUrl}
        lazy={true}
        fade={200}
        preloadDistance="100px"
        className="mx-5"
        placeholder={<div className="aspect-[4/3]  bg-gray-100" />}
      >
        <img
          src={enrichedImageUrl}
          alt={title}
          className="aspect-[4/3]  w-full rounded object-cover"
        />
      </ImagePreloader>
      <div className="mx-5 flex-1">
        <TextEffect
          as="h3"
          per="word"
          preset="fade"
          className="text-center font-serif text-[21px] font-medium leading-[24px] text-[#091F24]"
        >
          {title}
        </TextEffect>

        {note ? (
          <div
            style={
              sectionMinHeights?.note
                ? { minHeight: sectionMinHeights.note }
                : undefined
            }
          >
            <TextEffect
              per="word"
              preset="fade"
              className="text-center text-[12px] font-normal leading-[18px] text-[#091F24]"
            >
              {note}
            </TextEffect>
          </div>
        ) : null}
      </div>
      {hasPrices && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4, ease: "easeOut" }}
          className="flex shrink-0 flex-col items-center justify-center gap-1 bg-[#FAFAFA] px-2 py-3 text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5, ease: "easeOut" }}
            className="flex flex-wrap items-center justify-center"
          >
            <span className="block text-body3 leading-5 text-secondary">
              {formatMoney(priceCents)}
            </span>

            {typeof memberPriceCents === "number" && memberPriceCents > 0 && (
              <>
                <span className="block px-1 text-body3 leading-5">|</span>
                <span className="block text-body3 leading-5">
                  {formatMoney(memberPriceCents)} Member
                </span>
              </>
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.9, ease: "easeOut" }}
            className="text-xs text-primary underline"
            aria-label="View treatment details"
          >
            View
          </motion.div>
        </motion.div>
      )}
    </div>
  );

  if (href) {
    if (LinkComponent) {
      return (
        <LinkComponent
          to={href}
          className="block h-full transition-all duration-200 ease-out active:scale-[0.98]"
          onClick={handleClick}
        >
          {cardContent}
        </LinkComponent>
      );
    }
    return (
      <a
        href={href}
        className="block h-full transition-all duration-200 ease-out active:scale-[0.98]"
        onClick={handleClick}
      >
        {cardContent}
      </a>
    );
  }

  return cardContent;
};

export default React.memo(AdonisProductRecommendationCard);
